<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { ArrowLeft } from 'lucide-vue-next';
import { trackEvent } from '@app/composables/posthog';
import { useLandingSiteStore } from '@app/stores/landing-site';
import { goTo } from '@app/utils';

const props = withDefaults(defineProps<{ observeSelector?: string }>(), {
  observeSelector: '#hero',
});

const landingSiteStore = useLandingSiteStore();
const isHeroVisible = ref<boolean>(true);
let observer: IntersectionObserver | null = null;

// Only once the hero (which already carries a register CTA) is off screen, and never
// on top of the open mobile nav menu.
const isVisible = computed<boolean>(() => !isHeroVisible.value && !landingSiteStore.getMenu);

const goToRegister = (): void => {
  trackEvent('sticky_register_clicked', { source: 'sticky' });
  goTo('register');
};

onMounted(() => {
  const hero = document.querySelector(props.observeSelector);
  if (!hero) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      isHeroVisible.value = entry.isIntersecting;
    },
    { threshold: 0 }
  );
  observer.observe(hero);
});

onUnmounted(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <Transition name="sticky-cta">
    <div
      v-if="isVisible"
      dir="rtl"
      class="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_20px_rgba(15,23,42,0.08)] px-4 pt-3"
      :style="{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }"
    >
      <button
        @click="goToRegister"
        class="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white text-base px-6 py-3.5 rounded-full font-bold shadow-lg shadow-pink-500/30"
      >
        התחילו 14 יום חינם
        <ArrowLeft class="w-4 h-4 shrink-0" />
      </button>
      <p class="mt-1.5 text-center text-[11px] text-slate-500">ללא כרטיס אשראי · ביטול בכל עת</p>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.sticky-cta-enter-active,
.sticky-cta-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.sticky-cta-enter-from,
.sticky-cta-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
